import * as React from 'react';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import * as Globalization from '../ts/Globalization';
import { locale as devExtremeLocale } from 'devextreme/localization';

import ParameterFieldBase, {
    IDirectOptions,
    IParameterFieldBaseOptions,
    IValidator
} from '../../../_common/platform/ts/ParameterFieldBase';

import { IValuesCollection } from '../ts/appTheme';
import { ATTRIBUTE_TYPES } from '../ts/ConfigurationConstants';
import { getValueFormatsFromTheme } from '../ts/AttrQueryValuesFormat';
import DateBox from 'devextreme-react/date-box';
import { DateType } from 'devextreme/ui/date_box';
import { Format as dxFormat } from 'devextreme/common';

export type ValueFormatType = string | Date | string[] | Date[];
export type PickerType = 'calendar' | 'list' | 'native' | 'rollers';

export interface IDateFieldOptions extends IDirectOptions {
    type?: string;
    title?: string;
    placeholder?: string;
    allowNull?: boolean;

    displayDateFormat?: string;
    displayDateFormatNoChange?: boolean; //LL: 26.02.25
    displayDateTimeFormat?: string;

    formattedTime?: boolean; //LL: 09.11.18: default = false
    intervalMinutes?: number; //LL: 28.08.18: when type = time and  pickerType is "list"   30 def
    pickerType?: PickerType;
    nullSeconds?: boolean; //LL: 12.05.20

    useButtonsToApply?: boolean; // VR: show TODAY/APPLY/CANCEL buttons for 'calendar' type:
    // false or null:       do not show buttons, User selects date to close picker
    // true:                show buttons, user select data and press Apply or Cancel
    hideClearButton?: boolean; //LL: 21.03.18: default = false

    minDate?(): Date;
    maxDate?(): Date;
}

export interface IDateFieldProperties {
    value?: ValueFormatType;
    type?: string;

    title?: string;
    tooltip?: string;
    allowNull?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    //height?: string; // VR: height of DevEx control, like: "55px", "80%", "auto", "inherit" (also, DevEx allows: number, function)

    hideClearButton?: boolean; //LL: 24.02.18: default = false
    hint?: string; //LL: 24.02.18
    placeholder?: string;

    displayDateFormat?: string;
    displayDateFormatNoChange?: boolean; //LL: 26.02.25
    displayDateTimeFormat?: string;

    intervalMinutes?: number; //LL: 28.08.18: when type = time and  pickerType is "list"   30 def
    pickerType?: PickerType;
    useButtonsToApply?: boolean;
    useMaskBehavior?: boolean;
    dateOutOfRangeMessage?: string;

    theme?: IValuesCollection;
    options?: IDateFieldOptions;

    minDate?: Date;
    maxDate?: Date;

    onChange?(value: string): void;
    onValidationFail?(error: string): void;
    onFocus?(value: string): void;
    onBlur?(value: string): void;
}

function valueIsEmpty(value: ValueFormatType) {
    return value == null || value === '' || (typeof value === 'string' && value.toLowerCase() === 'invalid date');
}

function getValueType(props: IDateFieldProperties) {
    let result = props.type;

    if (result == null && props.options != null) {
        result = props.options.type;
    }

    return result || ATTRIBUTE_TYPES.DATE;
}

function isNullAllowed(props: IDateFieldProperties) {
    let allowNull = props.allowNull;

    if (allowNull == null && props.options != null) {
        allowNull = props.options.allowNull;
    }

    return allowNull !== false;
}

function checkValueForNull(value: ValueFormatType, props: IDateFieldProperties) {
    return !isNullAllowed(props) && valueIsEmpty(value);
}

function getUseButtonsToApply(props: IDateFieldProperties) {
    let result = props.useButtonsToApply;

    if (result == null && props.options != null) {
        result = props.options.useButtonsToApply;
    }

    return result;
}

function normalizeValue(value: ValueFormatType, props: IDateFieldProperties): string {
    if (valueIsEmpty(value)) {
        return null;
    }

    if (Array.isArray(value)) {
        value = value.length > 0 ? value[0] : null;

        if (valueIsEmpty(value)) {
            return null;
        }
    }

    const valueType = getValueType(props);

    if (valueType === ATTRIBUTE_TYPES.DATE) {
        return value as string;

        //LL: 09.11.18: introduced by RYB on 31.10.18: and it breaks my Chart code for "time"
        //LL: 09.11.18: to avoid it, use new option "formattedTime = true"
    } else if (valueType === ATTRIBUTE_TYPES.TIME && (props.options == null || props.options.formattedTime !== true)) {
        if (typeof value === 'string') {
            if (value.indexOf('T') < 0) {
                //like 00:00
                return value;
            } else if (value.indexOf('T') >= 0) {
                //like '1970-01-02T00:00:00'
                const indexOfTime = value.indexOf('T') + 1;

                return value.substring(indexOfTime);
            }
        } else {
            const hourT = value.getHours();
            const minuteT = value.getMinutes();

            return (
                (hourT < 10 ? '0' + hourT.toString() : hourT.toString()) +
                ':' +
                (minuteT < 10 ? '0' + minuteT.toString() : minuteT.toString()) +
                ':00'
            );
        }
    } else {
        let dateValue: Date;
        let hours: number;
        let minutes: number;
        let seconds: number;

        if (typeof value === 'string') {
            if (isNaN(Date.parse(value))) {
                if (isNullAllowed(props)) {
                    return null;
                }

                dateValue = new Date();
            } else {
                dateValue = new Date(value);
            }

            const findSemicolon = value.split(':'); //VR: ex: "2018-07-09T12:01:00+02:00"

            if (findSemicolon.length === 1) {
                //EVG: See Issue 84945.
                // Value has no time specified, but new Date() adds the local time. It should be zero.
                hours = 0;
                minutes = 0;
                seconds = 0;
            } else if (findSemicolon.length === 4) {
                //VR: Well, this VALUE already has been here - pass it AS IS!
                if (value.indexOf('+00:00') > 0) {
                    dateValue = new Date(value.slice(0, value.indexOf('+00:00')));
                }
            }
        } else if (typeof value === 'number') {
            dateValue = new Date(value);
        } else {
            dateValue = value;
        }

        const year = dateValue.getFullYear();
        const month = dateValue.getMonth() + 1;
        const day = dateValue.getDate();

        if (hours === undefined) {
            hours = dateValue.getHours();
        }

        if (minutes === undefined) {
            minutes = dateValue.getMinutes();
        }

        if (seconds === undefined) {
            seconds = dateValue.getSeconds();
        }

        let nullSeconds = false; //LL: 12.05.20

        if (props.options != null && props.options.nullSeconds != null) {
            nullSeconds = props.options.nullSeconds;
        }

        if (nullSeconds) {
            seconds = 0;
        }

        return (
            year.toString() +
            '-' +
            (month < 10 ? '0' + month.toString() : month.toString()) +
            '-' +
            (day < 10 ? '0' + day.toString() : day.toString()) +
            'T' +
            (hours < 10 ? '0' + hours.toString() : hours.toString()) +
            ':' +
            (minutes < 10 ? '0' + minutes.toString() : minutes.toString()) +
            ':' + //':00';
            (seconds < 10 ? '0' + seconds.toString() : seconds.toString())
        );
    }
}

function setCalendarStyle(cellData, cellElement, color, background) {
    if (cellElement && (color != null || background != null)) {
        const popupcontent = $(cellElement.closest('.dx-popup-content'));
        const css = {};

        if (color != null) {
            css['color'] = color;
        }

        if (background != null) {
            css['background'] = background;
        }

        popupcontent.css(css);
        $(cellElement.closest('.dx-popup-normal')).css(css);
        popupcontent
            .find(
                '.dx-button-content, \
                .dx-button-mode-contained,\
                .dx-popup-normal, .dx-popup-content, .dx-toolbar, .dx-button-content \
                .dx-calendar-navigator .dx-button .dx-button-text,\
                .dx-calendar-navigator .dx-button .dx-icon,\
                .dx-popup-wrapper>.dx-overlay-content,\
                .dx-calendar-body td, \
                .dx-calendar-body th, \
                .dx-calendar-body thead th, \
                .dx-calendar, \
                .dx-calendar-body, \
                .dx-calendar-cell, \
                .dx-calendar-views-wrapper table thead th'
            )
            .css(css);
        $(popupcontent).next()?.find('.dx-button-content')?.css(css);

        if (color != null) {
            cellElement.style.color = color;
        }
    }

    const span = document.createElement('span');

    span.textContent = cellData.text;
    cellElement.appendChild(span);
}

interface IState {
    value: string;
    oldPropsValue: string;
    locale: string;
}

function useState(props: IDateFieldProperties) {
    const state = React.useRef<IState>({
        value: normalizeValue(props.value, props),
        oldPropsValue: undefined,
        locale: devExtremeLocale()
    });
    const [, setUpdateFlag] = React.useState(0);

    const setValue = React.useCallback((newValue: string) => {
        state.current.value = newValue;
        setUpdateFlag((updateFlag) => updateFlag + 1);
    }, []);

    const setInitialValues = React.useCallback((newValue: string) => {
        if (state.current.oldPropsValue === undefined) {
            state.current.oldPropsValue = newValue;
        } else if (newValue !== state.current.oldPropsValue) {
            state.current.oldPropsValue = newValue;
            setValue(newValue);
        }
    }, []);

    const setLocale = React.useCallback((theme: IValuesCollection) => {
        if (theme == null) {
            return;
        }

        const widgetCulture = Globalization.getWidgetCulture(theme);
        const themeCulture = Globalization.getActualCulture(widgetCulture);
        const themeLocale = Globalization.getLocale(themeCulture);
        const appliedLocale = Globalization.getAppliedLocale();
        const dxLocale = devExtremeLocale();
        const locale = state.current.locale;

        if (locale !== themeLocale || locale !== appliedLocale || locale !== dxLocale) {
            Globalization.cultureLoader(themeCulture, (newLocale) => {
                state.current.locale = newLocale;
                devExtremeLocale(newLocale);
                setUpdateFlag((updateFlag) => updateFlag + 1);
            });
        }
    }, []);

    return { state: state.current, setInitialValues, setValue, setLocale };
}

export default function DateField(props: IDateFieldProperties) {
    const { state, setInitialValues, setValue, setLocale } = useState(props);
    const popupIsOpened = React.useRef(false);

    React.useEffect(() => {
        setInitialValues(normalizeValue(props.value, props));
        setLocale(props.theme ?? props.options?.theme);
    });

    const handleOnOptionChanged = React.useCallback(
        (e) => {
            if (e.name == 'isvalid' || e.name == 'validationError') {
                if (props.onValidationFail != null) {
                    props.onValidationFail(e.value?.message ?? '');
                }
            }
        },
        [props.onValidationFail]
    );

    const handleOnFocus = React.useCallback(
        (e: { component: any; element: any; event: any }) => {
            if (props.onFocus != null) {
                const value = e.event.target.value;

                props.onFocus(value);
            }
        },
        [props.onFocus]
    );

    const handleOnBlur = React.useCallback(
        (e: { component: any; element: any; event: any }) => {
            if (props.onBlur != null) {
                const value = e.event.target.value;

                props.onBlur(value);
            }
        },
        [props.onBlur]
    );

    const processOnChange = () => {
        const oldValue = normalizeValue(props.value, props);
        const newValue = normalizeValue(state.value, props);

        if (newValue !== oldValue) {
            props.onChange(newValue);
        }
    };

    const handleOnOpenedChange = (opened: boolean) => {
        const useButtonsToApply = getUseButtonsToApply(props);

        if (!useButtonsToApply && popupIsOpened.current && props.onChange != null) {
            processOnChange();
        }

        popupIsOpened.current = opened;
    };

    const handleOnValueChanged = (e: { component: any; element: any; value: any; previousValue: any }) => {
        const newValue = e.value;

        if (checkValueForNull(newValue, props)) {
            popupIsOpened.current = true;
            setValue(e.previousValue);
            return true;
        }

        if (state.value !== newValue) {
            setValue(newValue);

            if (props.onChange != null) {
                const useButtonsToApply = getUseButtonsToApply(props);

                if (useButtonsToApply || !popupIsOpened.current) {
                    processOnChange();
                }
            }
        }
    };

    const cellTemplate = (cellData, cellIndex, cellElement) => {
        if (cellElement != null) {
            setTimeout(() => {
                const color = props.theme?.['input-date-field-picker-color'];
                const background = props.theme?.['input-date-field-picker-bg'];

                setCalendarStyle(cellData, cellElement, color, background);
            }, 0);
        }
    };

    const options = props.options;
    const theme = props.theme ?? options?.theme;
    const valueType = getValueType(props);
    const value = state.value;

    let displayValue = valueIsEmpty(value) ? null : value;

    if (valueType === ATTRIBUTE_TYPES.TIME && (options == null || options.formattedTime !== true)) {
        if (typeof displayValue === 'string' && displayValue.indexOf('T') < 0 && displayValue.indexOf(':') > 0) {
            displayValue = '1970-01-02T' + value; //VR: add here YEAR, in other case DevExpress Edit always shows 00:00, instead value !!!
        }
    }

    let minDate = props.minDate;

    if (minDate == null && options != null && options.minDate != null) {
        minDate = options.minDate();
    }

    let maxDate = props.maxDate;

    if (maxDate == null && options != null && options.maxDate != null) {
        maxDate = options.maxDate();
    }

    let displayFormat: string;

    switch (valueType) {
        case ATTRIBUTE_TYPES.DATE:
            displayFormat = props.displayDateFormat;

            if (displayFormat == null && options != null) {
                displayFormat = options.displayDateFormat;
            }
            break;

        case ATTRIBUTE_TYPES.TIME:
            displayFormat = props.displayDateTimeFormat;

            if (displayFormat == null && options != null) {
                displayFormat = options.displayDateTimeFormat;
            }
            break;

        default:
            displayFormat = props.displayDateTimeFormat;

            if (displayFormat == null && options != null) {
                displayFormat = options.displayDateTimeFormat;
            }
    }

    const format = getValueFormatsFromTheme(valueType, theme);

    if (displayFormat == null && theme != null) {
        if (format.customFormat != null) {
            displayFormat = format.customFormat;
        } else if (format.formatFront != null) {
            displayFormat = format.formatFront;
        }
    }

    //EVG 14.02.2025: Not all formats are supported by DevExtreme.
    let dxDisplayFormat: dxFormat;

    if (displayFormat != null) {
        const formatSpecifier = Globalization.getFormatSpecifier(displayFormat, format.use24hours);

        if (typeof formatSpecifier !== 'string') {
            dxDisplayFormat = formatSpecifier.raw as any;
        }
    }

    if (dxDisplayFormat == null) {
        if (valueType === ATTRIBUTE_TYPES.TIME) {
            dxDisplayFormat = 'shortTime';
        } else if (valueType === ATTRIBUTE_TYPES.DATETIME) {
            dxDisplayFormat = 'shortDateShortTime';
        } else if (valueType === ATTRIBUTE_TYPES.DATE) {
            dxDisplayFormat = 'longDate';
            // //LL: 26.02.25 Why LONG ? in RecurrenceEdit: DateValueFormat = 'yyyy-MM-dd' and works well
            // if (props.displayDateFormatNoChange === true) {
            //     //dxDisplayFormat = displayFormat;
            // }
        }
    }

    //LL: 28.08.18: when type = time and  pickerType is "list"   30 def
    let intervalMinutes: number; // This option applies only if the type option is set to "time" and the pickerType is "list"

    if (valueType === ATTRIBUTE_TYPES.TIME) {
        intervalMinutes = props.intervalMinutes;

        if (intervalMinutes == null && options != null) {
            intervalMinutes = options.intervalMinutes;
        }
    }

    if (intervalMinutes == null) {
        intervalMinutes = 30;
    }

    let showClearButton = isNullAllowed(props);
    let hideClearButtonProp = props.hideClearButton;

    if (options != null && options.hideClearButton) {
        hideClearButtonProp = true;
    }

    if (hideClearButtonProp) {
        showClearButton = false;
    }

    let placeholder = props.placeholder;

    if (placeholder == null && options != null) {
        placeholder = options.placeholder;
    }

    // do not show buttons, User select date to close picker
    // show buttons, user select data and press Apply or Cancel
    let useMaskBehavior = props.useMaskBehavior;

    if (useMaskBehavior == null) {
        useMaskBehavior = valueType === ATTRIBUTE_TYPES.TIME || valueType === ATTRIBUTE_TYPES.DATETIME;
    }

    let dateBoxType: DateType;

    switch (valueType) {
        case ATTRIBUTE_TYPES.DATETIME:
            dateBoxType = 'datetime';
            break;

        case ATTRIBUTE_TYPES.TIME:
            dateBoxType = 'time';
            break;

        default:
            dateBoxType = 'date';
            break;
    }

    const title = options != null ? options.title : props.title;

    let titleElement: React.JSX.Element;

    if (title != null && title === '') {
        titleElement = <label>{title}</label>;
    }

    const readOnly = props.readOnly ?? options?.readOnly;
    const useButtonsToApply = getUseButtonsToApply(props);

    return (
        <div className="date-field" title={props.tooltip}>
            {titleElement}

            <div className="date-field-container">
                <DateBox
                    type={dateBoxType}
                    value={displayValue}
                    showClearButton={showClearButton}
                    readOnly={readOnly}
                    disabled={props.disabled === true}
                    placeholder={placeholder}
                    validationMessageMode={'always'} // VR, 15 Dec 2020, Leo asked to show invalid string ALWAYS after focus moved from DatePicker
                    displayFormat={props.displayDateFormatNoChange ? displayFormat : dxDisplayFormat} //LL: 26.02.25
                    //dateSerializationFormat = { 'yyyy-MM-ddTHH:mm:ssZ' }
                    acceptCustomValue={true}
                    //openOnFieldClick = { false }  // ONLY since DevExpress 18.2
                    useMaskBehavior={useMaskBehavior} // ONLY since DevExpress 18.2, but was with bugs until 19 or 20
                    pickerType={'calendar'}
                    applyValueMode={useButtonsToApply ? 'useButtons' : 'instantly'}
                    hint={props.hint == null ? null : props.hint}
                    min={minDate}
                    max={maxDate}
                    interval={intervalMinutes}
                    width={'100%'}
                    height={'26px'} // VR: Let it be ALWAYS 29px - standard for our INPUT controld.   (OLD:'95%') OLD=29px,
                    dateOutOfRangeMessage={props.dateOutOfRangeMessage}
                    calendarOptions={{
                        cellTemplate: cellTemplate
                    }}
                    onOptionChanged={handleOnOptionChanged}
                    onValueChanged={handleOnValueChanged}
                    onFocusIn={handleOnFocus}
                    onFocusOut={handleOnBlur}
                    onOpenedChange={handleOnOpenedChange}
                />
            </div>
        </div>
    );
}

export interface IParameterDateFieldOptions extends IParameterFieldBaseOptions, IDateFieldOptions {}

export class DateTimeField extends ParameterFieldBase<IParameterDateFieldOptions> {
    public static EditComponent = DateField;
    public getDefaultOptions() {
        return {};
    }

    constructor(
        glob: IGlob,
        title: string,
        validators?: IValidator[],
        defaultVal?: any,
        options?: IParameterDateFieldOptions
    ) {
        super(glob, title, [], validators, defaultVal, options);
    }
}
